import React from 'react';

import { Box, Button, H1, HStack, Icon, Separator, Text, VStack } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';
import { Form, Formik } from 'formik';

import { FormikFileUpload } from '../../formik/FormikFileUpload';
import { CustomFundLineupLayout } from '../CustomFundLineupLayout';

interface CsvError {
  row: number;
  input: string;
  issue: string;
}

const HARDCODED_ERRORS: CsvError[] = [
  { row: 1, input: 'VIVLX', issue: 'Ticker not supported' },
  { row: 12, input: 'TRPAX', issue: 'Ticker not supported' },
  { row: 14, input: '0910', issue: 'Invalid ticker format' },
  { row: 23, input: 'STRKQ', issue: 'Invalid ticker format' },
];

interface ResolveIssuesStepProps {
  csvErrors?: CsvError[];
}

interface ResolveIssuesFormValues {
  fundLineupFile: File | null;
}

export const ResolveIssuesStep: React.FC<ResolveIssuesStepProps> = ({ csvErrors = HARDCODED_ERRORS }) => {
  const initialValues: ResolveIssuesFormValues = {
    fundLineupFile: null,
  };

  const handleSubmit = (_values: ResolveIssuesFormValues) => {};

  return (
    <CustomFundLineupLayout>
      <Formik initialValues={initialValues} onSubmit={handleSubmit}>
        <Form style={{ flex: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box css={{ flex: 1, display: 'flex', flexDirection: 'column', py: 'space.600', px: 'space.600' }}>
            <VStack css={{ gap: 'space.400', maxWidth: '1200px', mx: 'auto', width: '100%' }}>
              <VStack css={{ gap: 'space.100' }}>
                <H1 variant="title400">Resolve issue(s) and re-upload.</H1>
                <Text variant="text100" css={{ color: 'text.secondary' }}>
                  We found {csvErrors.length} issues in your fund lineup. Fix errors and re-upload the file.
                </Text>
              </VStack>

              <HStack css={{ gap: 'space.400', alignItems: 'flex-start' }}>
                <Box
                  css={{
                    flex: 1,
                    border: '1px solid',
                    borderColor: 'border.weak',
                    borderRadius: 'medium',
                    bg: 'background.surface',
                    overflow: 'hidden',
                  }}
                >
                  <HStack css={{ px: 'space.300', py: 'space.200' }}>
                    <Box css={{ width: '80px' }}>
                      <Text css={{ textStyle: 'title100' }}>Row</Text>
                    </Box>
                    <Box css={{ flex: 1 }}>
                      <Text css={{ textStyle: 'title100' }}>Input</Text>
                    </Box>
                    <Box css={{ flex: 2 }}>
                      <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
                        <Icon name="WarningFilled" size="small" color="icon.warning" />
                        <Text
                          css={{
                            textStyle: 'title100',
                            color: 'text.warning',
                          }}
                        >
                          Issue(s)
                        </Text>
                      </HStack>
                    </Box>
                  </HStack>

                  <Separator />

                  {csvErrors.map((error, index) => (
                    <React.Fragment key={error.row}>
                      <HStack css={{ px: 'space.300', py: 'space.300' }}>
                        <Box css={{ width: '80px' }}>
                          <Text variant="text100">{error.row}</Text>
                        </Box>
                        <Box css={{ flex: 1 }}>
                          <Text variant="text100">{error.input}</Text>
                        </Box>
                        <Box css={{ flex: 2 }}>
                          <Text variant="text100">{error.issue}</Text>
                        </Box>
                      </HStack>
                      {index < csvErrors.length - 1 && <Separator />}
                    </React.Fragment>
                  ))}
                </Box>

                <VStack css={{ width: '360px', gap: 'space.300' }}>
                  <Box
                    css={{
                      border: '1px solid',
                      borderColor: 'border.weak',
                      borderRadius: 'medium',
                      p: 'space.300',
                      bg: 'background.surface',
                    }}
                  >
                    <VStack css={{ gap: 'space.200' }}>
                      <Text variant="text100">Upload fund lineup CSV</Text>
                      <FormikFileUpload name="fundLineupFile" acceptedFileTypes=".csv" />
                      <Button
                        type="submit"
                        variant="solidPrimary"
                        css={{ minWidth: '120px', minHeight: 'size.600', alignSelf: 'flex-start' }}
                      >
                        Retry
                      </Button>
                    </VStack>
                  </Box>

                  <Box
                    css={{
                      border: '1px solid',
                      borderColor: 'border.weak',
                      borderRadius: 'medium',
                      p: 'space.300',
                      bg: 'background.surface',
                    }}
                  >
                    <VStack css={{ gap: 'space.200' }}>
                      <Text css={{ textStyle: 'title100' }}>Guidelines</Text>
                      <ul style={{ paddingLeft: '16px', margin: 0 }}>
                        <li style={{ marginBottom: '8px' }}>
                          <Text variant="text100">
                            Do not modify the template's format. Add tickers to the fund lineup in column A. Designate a
                            ticker as a default investment by writing "QDIA" in column B.
                          </Text>
                        </li>
                        <li style={{ marginBottom: '8px' }}>
                          <Text variant="text100">
                            We require a minimum of one Target Date Fund to be designated by default. Participants can
                            select any mix of funds within your specified lineup, though.
                          </Text>
                        </li>
                        <li>
                          <Text variant="text100">
                            If you do not see a ticker you wish to add or need support, you may{' '}
                            <SambaAutoLink to="/support" target="_blank" rel="noopener noreferrer">
                              contact us
                            </SambaAutoLink>
                            .
                          </Text>
                        </li>
                      </ul>
                    </VStack>
                  </Box>
                </VStack>
              </HStack>
            </VStack>
          </Box>
        </Form>
      </Formik>
    </CustomFundLineupLayout>
  );
};
